import {
  BoundSwitchField,
  BoundTreeSelectField,
  Button,
  Css,
  FormPageLayout,
  Icon,
  IconButton,
  MultiSelectField,
  Palette,
  ToggleChipGroup,
  useSnackbar,
  useTestIds,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { observer } from "mobx-react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { PotentialOperation2, SaveReadyPlanRequiredOptionInput } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { DesignCatalogParams } from "src/routes/routesDef";
import { createDesignPackagesUrl, createDesignPackageUrl } from "src/RouteUrls";
import { queriesResult } from "src/utils";
import {
  DesignPackageRequiredOptionsPage_AddOnsFragment,
  DesignPackageRequiredOptionsPage_DesignPackageFragment,
  DesignPackageRequiredOptionsPage_TargetGroupsFragment,
  useDesignPackageRequiredOptionsPage_AddOnsQuery,
  useDesignPackageRequiredOptionsPage_CombinationsQuery,
  useDesignPackageRequiredOptionsPageQuery,
  useSaveDesignPackageRequiredOptionsMutation,
} from "./DesignPackageRequiredOptionsPage.generated";

export function DesignPackageRequiredOptionsPage() {
  const { designPackageId, designPackageVersionId } = useRouteParams<DesignCatalogParams>();
  const requiredOptionsQuery = useDesignPackageRequiredOptionsPageQuery({
    variables: { designPackageId, designPackageVersionId },
  });
  const addOnsQuery = useDesignPackageRequiredOptionsPage_AddOnsQuery({
    variables: { designPackageId, designPackageVersionId },
  });

  // Map required options query to form state
  const formState = useFormState({
    config: formConfig,
    init: {
      query: requiredOptionsQuery,
      map: ({ designPackage }) => ({
        requiredOptions: designPackage.requiredOptions.nonEmpty
          ? designPackage.requiredOptions.map((option) => ({
              id: option.id,
              active: option.active,
              usePersonalizationGroups: option.usePersonalizationGroups,
              designPackageId: designPackage.id,
              targetGroupIds: option.targetGroups.map((group) => group.id),
              targetCombinations: option.targetCombinations.map((combination) => combination.id),
              planAddOnIds: option.planAddOns.map((rpo) => rpo.id),
            }))
          : designPackage.canEdit.allowed
            ? [blankRequiredOption(designPackage.id)] // Start with an empty row if none exist and user has edit permissions
            : [], // Otherwise, don't show any rows
      }),
    },
  });

  return queriesResult([requiredOptionsQuery, addOnsQuery] as const, {
    data: (requiredOptionsData, addOnsData) => (
      <DesignPackageRequiredOptionsDataView
        designPackage={requiredOptionsData.designPackage}
        designPackageVersionId={designPackageVersionId}
        potentialAddOnOptions={addOnsData.designPackage.potentialAddOnOptions}
        formState={formState}
      />
    ),
  });
}

type AddOnOptionsTree = { id: string; name: string; children?: AddOnOptionsTree[] };

type FormStateRequiredOption = Omit<SaveReadyPlanRequiredOptionInput, "targetCombinations"> & {
  targetCombinations: string[];
};
type FormStateConfig = { requiredOptions: FormStateRequiredOption[] };

type DesignPackageRequiredOptionsDataViewProps = {
  designPackage: DesignPackageRequiredOptionsPage_DesignPackageFragment;
  designPackageVersionId: string;
  potentialAddOnOptions: DesignPackageRequiredOptionsPage_AddOnsFragment[];
  formState: ObjectState<FormStateConfig>;
};

const DesignPackageRequiredOptionsDataView = observer((props: DesignPackageRequiredOptionsDataViewProps) => {
  const { designPackage, designPackageVersionId, potentialAddOnOptions, formState } = props;
  const addOnOptionsTree = useMemo(() => getAddOnOptionsTree(potentialAddOnOptions), [potentialAddOnOptions]);
  const tid = useTestIds({});
  const navigate = useNavigate();
  const { triggerNotice } = useSnackbar();

  const [saveRequiredOptions] = useSaveDesignPackageRequiredOptionsMutation();
  async function handleSave() {
    const result = await saveRequiredOptions({
      variables: {
        input: formState.requiredOptions.changedValue.map(({ targetCombinations, ...rest }) => ({
          ...rest,
          ...(targetCombinations && { targetCombinations: targetCombinations.map((c) => ({ options: c.split(",") })) }),
        })),
      },
    });
    triggerNotice({ message: "Required Add-Ons successfully saved", icon: "success" });
    navigate(
      createDesignPackageUrl(
        designPackage.id,
        // If new draft version was created, navigate back to that
        result.data?.saveReadyPlanRequiredOptions.readyPlanRequiredOptions.first?.readyPlan.aggregateLatest.id ??
          designPackageVersionId,
      ),
      { replace: true },
    );
  }

  // The target groups are likely already sorted by the BE, but just in case, sort here too.
  const targetGroups = useMemo(
    () => designPackage.availableTargetGroups.sortBy((tg) => tg.order),
    [designPackage.availableTargetGroups],
  );

  const personalizationTargetGroups = useMemo(
    () => designPackage.optionGroups.filter((rpog) => rpog.personalizationType),
    [designPackage.optionGroups],
  );

  const addAnotherPairing = useCallback(() => {
    formState.requiredOptions.add(blankRequiredOption(designPackage.id));
  }, [formState, designPackage]);
  const removePairing = useCallback(
    (rowState: ObjectState<FormStateRequiredOption>) => {
      if (rowState.id.value) {
        rowState.active.set(false);
      } else {
        formState.requiredOptions.remove(rowState.value);
      }
    },
    [formState],
  );

  return (
    <FormPageLayout
      pageTitle={"Edit Required Add-Ons"}
      formState={formState}
      breadCrumb={[
        { label: "Design Packages", href: createDesignPackagesUrl() },
        { label: designPackage.name, href: createDesignPackageUrl(designPackage.id, designPackageVersionId) },
      ]}
      formSections={[
        {
          rows: [
            {
              reactNodeFoo: (
                <div {...tid.header}>
                  <div css={Css.xl.mb2.$}>Required Add-Ons</div>
                  <div css={Css.gray700.$}>
                    Depending on which Design Package you select, some Add-Ons are required. Select the required
                    add-on(s) per each design package below. If there are none, leave blank.
                  </div>
                </div>
              ),
            },
          ],
        },
        ...(formState.requiredOptions.rows.nonEmpty
          ? formState.requiredOptions.rows
              .filter((row) => row.active.value)
              // Exclude inactive rows
              .map((row, index) => ({
                rows: [
                  {
                    reactNodeFoo: (
                      <AddEditRequiredOptionSection
                        index={index + 1}
                        rowState={row}
                        targetGroups={targetGroups}
                        personalizationTargetGroups={personalizationTargetGroups}
                        addOnOptionsTree={addOnOptionsTree}
                        canEdit={designPackage.canEdit}
                        onDelete={removePairing}
                      />
                    ),
                  },
                ],
              }))
          : [
              {
                rows: [
                  {
                    reactNodeFoo: (
                      <div css={Css.gray600.df.aic.gap1.$}>
                        <Icon icon="infoCircle" />
                        There are no required add-ons configured in the selected version
                      </div>
                    ),
                  },
                ],
              },
            ]),
        {
          rows: [
            {
              reactNodeFoo: (
                <Button
                  label="Add Another Pairing"
                  variant="primary"
                  onClick={addAnotherPairing}
                  disabled={disableBasedOnPotentialOperation(designPackage.canEdit)}
                />
              ),
            },
          ],
        },
      ]}
      cancelAction={{ label: "Cancel", onClick: createDesignPackageUrl(designPackage.id, designPackageVersionId) }}
      submitAction={{
        label: "Save",
        onClick: handleSave,
        disabled: !formState.valid || disableBasedOnPotentialOperation(designPackage.canEdit),
      }}
    />
  );
});

type AddEditRequiredOptionSectionProps = {
  index: number;
  rowState: ObjectState<
    Omit<SaveReadyPlanRequiredOptionInput, "targetCombinations"> & { targetCombinations: string[] }
  >;
  targetGroups: DesignPackageRequiredOptionsPage_TargetGroupsFragment[];
  personalizationTargetGroups: DesignPackageRequiredOptionsPage_TargetGroupsFragment[];
  addOnOptionsTree: AddOnOptionsTree[];
  canEdit: PotentialOperation2;
  onDelete: (rowState: ObjectState<FormStateRequiredOption>) => void;
};

const AddEditRequiredOptionSection = observer((props: AddEditRequiredOptionSectionProps) => {
  const { designPackageId, designPackageVersionId } = useRouteParams<DesignCatalogParams>();
  const {
    index,
    rowState,
    targetGroups: primaryTargetGroups,
    personalizationTargetGroups,
    addOnOptionsTree,
    canEdit,
    onDelete,
  } = props;
  const readOnly = !canEdit.allowed;
  const usePersonalization = rowState.usePersonalizationGroups.value;
  // Get option combinations based on the selected target groups
  const { data } = useDesignPackageRequiredOptionsPage_CombinationsQuery({
    variables: {
      dpId: designPackageId,
      rpavId: designPackageVersionId,
      targetGroupIds: rowState.targetGroupIds.value || [],
      // Create a cartesian product of all target groups when personalization is enabled
      // This will skip the option prerequisites needed
      fullCartesian: usePersonalization ?? false,
    },
    skip: !rowState.targetGroupIds.value || rowState.targetGroupIds.value.isEmpty,
  });
  const tid = useTestIds({});
  const targetCombinations = data?.designPackage.combinations ?? [];

  // When use personalization is on, show both primary and personalization groups
  const targetGroups = useMemo(() => {
    const rpogs = usePersonalization ? [...primaryTargetGroups, ...personalizationTargetGroups] : primaryTargetGroups;
    return rpogs.sortBy((rpog) => rpog.order);
  }, [usePersonalization, primaryTargetGroups, personalizationTargetGroups]);

  // Shared handler for target group selection — ensures IDs are stored in group order and clears combinations
  const handleTargetGroupChange = useCallback(
    (tgIds: string[]) => {
      rowState.targetGroupIds.set(targetGroups.filter((tg) => tgIds.includes(tg.id)).map((tg) => tg.id));
      rowState.targetCombinations.set([]);
    },
    [rowState, targetGroups],
  );

  return (
    <div css={Css.bb.bcGray200.w0.mw("100%").$} {...tid.pairing}>
      <div css={Css.df.jcsb.aic.lg.$}>
        <div css={Css.df.aic.gap2.$}>
          <div>Pairing {index}</div>
          <BoundSwitchField
            {...tid.pairingUsePersonalization}
            label="Use Personalization"
            labelStyle="filter"
            field={rowState.usePersonalizationGroups}
            disabled={readOnly}
            onChange={(val) => {
              rowState.usePersonalizationGroups.set(val);
              // Clear target groups and combinations when toggling
              rowState.targetGroupIds.set([]);
              rowState.targetCombinations.set([]);
            }}
          />
        </div>
        <IconButton
          {...tid.pairingDelete}
          tooltip="Remove this pairing."
          icon="x"
          disabled={disableBasedOnPotentialOperation(canEdit)}
          color={Palette.Gray500}
          onClick={() => onDelete(rowState)}
        />
      </div>
      <div css={Css.df.gap2.mt2.$}>
        <div css={Css.w50.$}>
          {usePersonalization ? (
            <MultiSelectField
              {...tid.pairingApplyTo}
              label="Apply To"
              values={rowState.targetGroupIds?.value ?? []}
              options={targetGroups}
              autoSort={false}
              readOnly={readOnly}
              onSelect={(tgIds) => handleTargetGroupChange(tgIds)}
            />
          ) : (
            <ToggleChipGroup
              {...tid.pairingApplyTo}
              label="Apply To"
              values={rowState.targetGroupIds?.value ?? []}
              options={targetGroups.map((tg) => ({ label: tg.name, value: tg.id }))}
              readonly={readOnly}
              onChange={(tgIds) => handleTargetGroupChange(tgIds)}
            />
          )}
        </div>
        <div css={Css.w50.$}>
          <MultiSelectField
            {...tid.pairingOptionCombinations}
            label={
              rowState.targetGroupIds.value?.nonEmpty
                ? rowState.targetGroupIds.value.map((id) => targetGroups.find((tg) => tg.id === id)?.name).join(" x ")
                : "--"
            }
            disabled={targetCombinations.isEmpty}
            options={targetCombinations}
            placeholder="Select Option Combinations"
            readOnly={readOnly}
            onSelect={(val) => rowState.targetCombinations.set(val)}
            values={rowState.targetCombinations.value}
          />
        </div>
      </div>
      <div css={Css.mt3.$}>
        <BoundTreeSelectField
          {...tid.pairingRequiredAddOns}
          field={rowState.planAddOnIds}
          label="Required Add-Ons"
          options={addOnOptionsTree}
          defaultCollapsed
          // Use the `leaf` values so if a user selects an asset, store the asset selection (vs storing the parent business function)
          onSelect={(options) => rowState.planAddOnIds.set(options.leaf.values)}
          placeholder="--"
          chipDisplay="root"
          readOnly={readOnly}
        />
      </div>
    </div>
  );
});

function getAddOnOptionsTree(addOnOptions: DesignPackageRequiredOptionsPage_AddOnsFragment[]): AddOnOptionsTree[] {
  return addOnOptions
    .groupByObject((opt) => opt.code)
    .map(([code, opts]) => ({
      id: code,
      name: `${opts.first!.name} - ${code}`,
      children: opts
        .groupByObject((opt) => opt.readyPlan.id)
        .map(([rpId, rpOpts]) => {
          if (rpOpts.length === 1) {
            // If only one option, don't bother showing child locations
            return {
              id: rpOpts.first!.id,
              name: `${code} - ${rpOpts.first!.readyPlan.name} @ ${rpOpts.first!.location?.name ?? "Whole House"}`,
            };
          } else {
            return {
              id: `${rpId}-${code}`,
              name: `${rpOpts.first!.readyPlan.code} - ${rpOpts.first!.readyPlan.name}`,
              children: rpOpts
                .map((opt) => ({
                  id: opt.id,
                  name: `${code} - ${opt.readyPlan.name} @ ${opt.location?.name ?? "Whole House"}`,
                }))
                .sortByKey("name"),
            };
          }
        })
        .sortByKey("name"),
    }))
    .sortByKey("name");
}

function blankRequiredOption(designPackageId: string): FormStateRequiredOption {
  return {
    id: null,
    active: true,
    usePersonalizationGroups: false,
    designPackageId,
    planAddOnIds: [],
    targetCombinations: [],
    targetGroupIds: [],
  };
}

const formConfig: ObjectConfig<FormStateConfig> = {
  requiredOptions: {
    update: "incremental",
    type: "list",
    config: {
      id: { type: "value" },
      active: { type: "value" },
      usePersonalizationGroups: { type: "value" },
      designPackageId: { type: "value" },
      targetGroupIds: { type: "value" },
      planAddOnIds: { type: "value" },
      targetCombinations: { type: "value" },
    },
  },
};
